import _pick from 'lodash/pick';
import { AttachmentIntendedUse } from '@lib/enums/attachments';
import { AttachmentWithIntendedUse } from './findAttachmentByIntendedUse';

/**
 * Input object for attachment mutations
 */
export interface AttachmentInput {
  id?: string;
  file?: unknown;
  filename?: string;
  metadata?: {
    intendedUse: AttachmentIntendedUse;
  };
  _destroy?: boolean;
}

/**
 * Prepares attachment inputs for mutation based on form value and original attachment.
 * Returns array of attachment inputs (can include both destruction and new upload).
 * Similar to mergeAndUnlinkAttachedFiles but for single attachment with intendedUse.
 *
 * Cases handled:
 * 1. No form value and no original → empty array (nothing to do)
 * 2. No form value but had original → array with destruction marker
 * 3a. New upload (has file) → array with new upload (and destruction of old if replacing)
 * 3b. Existing unchanged (has urls, no file) → empty array (don't include)
 *
 * @param formValue - Current form value for the attachment field
 * @param originalAttachment - Original attachment from the template/entity
 * @param intendedUse - The intendedUse to set in metadata
 * @returns Array of attachment input objects for mutation
 */
export default function prepareAttachmentInputs(
  formValue: Record<string, unknown> | null | undefined,
  originalAttachment: AttachmentWithIntendedUse | undefined,
  intendedUse: AttachmentIntendedUse,
): AttachmentInput[] {
  const result: AttachmentInput[] = [];

  // Case 1: No form value and no original → nothing to do
  if (!formValue && !originalAttachment) {
    return result;
  }

  // Case 2: No form value but had original → mark for destruction
  if (!formValue && originalAttachment) {
    result.push({ id: originalAttachment.id, _destroy: true });
    return result;
  }

  // Case 3: Has form value
  if (formValue) {
    const hasFile = Boolean(formValue.file);
    const hasUrls = Boolean(formValue.urls);
    const formValueId = formValue.id as string | undefined;

    // Case 3a: New upload (has file)
    if (hasFile) {
      // If replacing an existing attachment (different id), mark old for destruction
      if (originalAttachment && originalAttachment.id !== formValueId) {
        result.push({ id: originalAttachment.id, _destroy: true });
      }
      // Add new upload with intendedUse metadata
      result.push({
        ..._pick(formValue, ['id', 'file', 'filename']),
        metadata: { intendedUse },
      } as AttachmentInput);
      return result;
    }

    // Case 3b: Existing unchanged (has urls, no file) → don't include
    if (hasUrls && !hasFile) {
      return result;
    }
  }

  return result;
}
